import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { closestTextMatch } from '../../utils/closest-match';

/**
 * Buscador tipo "autocomplete" de un solo valor (departamento/ciudad): se
 * escribe para filtrar sugerencias y se elige una con click/Enter. Con
 * [allowCustom]="false" (departamento) el valor solo puede ser uno de
 * [options] -- igual que el selector de bancos, exige una selección real.
 * Con [allowCustom]="true" (ciudad) el texto escrito también se acepta tal
 * cual al salir del campo, para no bloquear ciudades aún no registradas.
 */
@Component({
  selector: 'app-location-autocomplete',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="loc-ac">
      <div class="loc-ac-control" [class.invalid]="invalid" [class.disabled]="disabled">
        <i class="bi bi-geo-alt" aria-hidden="true"></i>
        <input
          #searchInput
          type="text"
          [(ngModel)]="displayText"
          (focus)="open()"
          (blur)="onBlur()"
          (input)="onType()"
          (keydown)="onKeydown($event)"
          [placeholder]="placeholder"
          [disabled]="disabled"
          autocomplete="off"
        >
      </div>
      <div class="loc-ac-panel" *ngIf="isOpen() && !disabled">
        <button
          type="button"
          *ngFor="let option of filtered(); let i = index; trackBy: trackOption"
          class="loc-ac-option"
          [class.active]="i === activeIndex()"
          (mousedown)="$event.preventDefault(); pick(option)"
        >{{ option }}</button>
        <p class="loc-ac-empty" *ngIf="!filtered().length">{{ allowCustom ? 'Sin coincidencias. Puedes escribir un valor nuevo.' : 'Sin coincidencias.' }}</p>
      </div>
    </div>
  `,
  styles: [`
    .loc-ac{position:relative;display:block}
    .loc-ac-control{position:relative;display:flex;align-items:center;gap:.55rem;box-sizing:border-box;width:100%;min-height:48px;padding:0 .8rem;border:1px solid var(--ts-border-strong,rgba(148,173,207,.28));border-radius:9px;background:var(--ts-input-bg,#071a31);transition:border-color .15s ease,box-shadow .15s ease}
    .loc-ac-control:focus-within{border-color:var(--ts-primary,#2f7cff);box-shadow:0 0 0 3px var(--ts-primary-soft-14,rgba(47,124,255,.14))}
    .loc-ac-control.invalid{border-color:var(--ts-danger,#f05259)}
    .loc-ac-control.disabled{opacity:.55;cursor:not-allowed}
    .loc-ac-control>i:first-child{color:var(--ts-placeholder,#71849e);font-size:.9rem}
    .loc-ac-control input{flex:1;min-width:0;height:46px;margin:0;padding:0;border:0 !important;outline:0 !important;box-shadow:none !important;background:transparent !important;color:var(--ts-text,#f4f8ff);font:inherit;font-size:.9rem}
    .loc-ac-control input::placeholder{color:var(--ts-placeholder,#71849e)}
    .loc-ac-control input:disabled{cursor:not-allowed}
    .loc-ac-panel{position:absolute;z-index:30;top:calc(100% + 6px);left:0;right:0;max-height:260px;overflow-y:auto;box-sizing:border-box;padding:.4rem;border:1px solid var(--ts-border-strong,rgba(148,173,207,.28));border-radius:11px;background:var(--ts-bg-elevated,#0a1c34);box-shadow:var(--ts-shadow-md,0 14px 36px rgba(0,7,22,.24))}
    .loc-ac-option{display:block;width:100%;box-sizing:border-box;padding:.6rem .7rem;border:0;border-radius:8px;background:transparent;color:var(--ts-text,#f4f8ff);font-size:.86rem;text-align:left;cursor:pointer}
    .loc-ac-option:hover,.loc-ac-option.active{background:var(--ts-primary-soft-14,rgba(47,124,255,.14))}
    .loc-ac-empty{margin:0;padding:.5rem .7rem;color:var(--ts-text-muted,#91a4bf);font-size:.8rem}
  `],
})
export class LocationAutocompleteComponent implements OnChanges {
  @Input() options: string[] = [];
  @Input() value = '';
  @Input() allowCustom = true;
  @Input() invalid = false;
  @Input() disabled = false;
  @Input() placeholder = 'Buscar…';
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;

  displayText = '';
  isOpen = signal(false);
  activeIndex = signal(-1);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !this.isOpen()) {
      this.displayText = this.value || '';
    }
  }

  filtered(): string[] {
    const term = this.displayText.trim().toLowerCase();
    if (!term) return this.options;
    return this.options.filter(option => option.toLowerCase().includes(term));
  }

  open(): void {
    if (this.disabled) return;
    this.isOpen.set(true);
    this.activeIndex.set(-1);
  }

  onType(): void {
    this.open();
  }

  onBlur(): void {
    this.resolveTypedSelection();
  }

  /** Se ejecuta al perder el foco (click fuera) o al presionar Enter sin
   * haber navegado a una opción con las flechas. Sin allowCustom el valor
   * debe ser una opción real del catálogo: si no hay coincidencia exacta, se
   * autoselecciona la más cercana a lo escrito (p. ej. "san sal" -> "San
   * Salvador") en vez de perder el texto tecleado y revertir al valor
   * anterior. Con allowCustom el texto escrito se acepta tal cual. */
  private resolveTypedSelection(): void {
    this.isOpen.set(false);
    const typed = this.displayText.trim();
    if (this.allowCustom) {
      this.displayText = typed;
      if (typed !== this.value) this.valueChange.emit(typed);
      return;
    }
    const match = this.closestMatch(typed);
    if (match) {
      this.displayText = match;
      if (match !== this.value) this.valueChange.emit(match);
    } else {
      this.displayText = this.value || '';
    }
  }

  private closestMatch(term: string): string | null {
    return closestTextMatch(term, this.options, option => option);
  }

  pick(option: string): void {
    this.displayText = option;
    this.isOpen.set(false);
    this.activeIndex.set(-1);
    if (option !== this.value) this.valueChange.emit(option);
  }

  onKeydown(evt: KeyboardEvent): void {
    const options = this.filtered();
    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      this.open();
      this.activeIndex.set(Math.min(this.activeIndex() + 1, options.length - 1));
    } else if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      this.activeIndex.set(Math.max(this.activeIndex() - 1, 0));
    } else if (evt.key === 'Enter') {
      const option = options[this.activeIndex()];
      evt.preventDefault();
      if (option) {
        this.pick(option);
      } else {
        this.resolveTypedSelection();
      }
    } else if (evt.key === 'Escape') {
      this.isOpen.set(false);
    }
  }

  trackOption(_: number, option: string): string {
    return option;
  }

  focusSearch(): void {
    const target = this.searchInputRef?.nativeElement;
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('field-error-flash');
    target.focus();
    setTimeout(() => target.classList.remove('field-error-flash'), 1900);
  }
}
